import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";

interface Props {
  /** Initial markdown value — loaded into the editor on mount. */
  value: string;
  /** Fires on every keystroke with the round-tripped markdown. */
  onChange: (markdown: string) => void;
  /** Read-only when true (e.g. executive role). */
  disabled?: boolean;
  /** Accessible name. Required — every editable field must have one. */
  ariaLabel: string;
  /** Placeholder shown when empty. */
  placeholder?: string;
  /** Optional minimum visual height (px). Defaults to a 4-row textarea height. */
  minHeight?: number;
}

/**
 * OAIP-114: always-on WYSIWYG editor for project markdown fields. Replaces
 * the previous `Input.TextArea + <ReactMarkdown>` pair that rendered the
 * same content twice on the Project Details tab.
 *
 * Uses TipTap + tiptap-markdown so the persisted value stays plain markdown,
 * compatible with everywhere else that renders project text (Apex chat
 * tools, exec dashboard, search highlighting). Configuration mirrors
 * `components/workflow/BriefEditor.tsx` so the editing experience matches
 * the brief editor staff already use.
 */
export default function MarkdownField({
  value,
  onChange,
  disabled,
  ariaLabel,
  placeholder,
  minHeight = 96,
}: Props) {
  // tiptap-markdown's serializer normalises an empty editor to "\n" rather
  // than "", and may strip/add trailing whitespace on round-trip. Compare
  // editor output against the initial prop after stripping trailing
  // whitespace so a mount-time normalisation never fires `onChange` and
  // never produces a no-op PATCH / audit entry.
  const initialValueRef = useRef<string>(value || "");
  const normalise = (s: string) => (s || "").replace(/\s+$/, "");
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      } as any),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Markdown.configure({
        html: false,
        tightLists: true,
        bulletListMarker: "-",
        linkify: true,
        breaks: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        role: "textbox",
        "aria-multiline": "true",
        style: `min-height: ${minHeight}px; outline: none;`,
      },
    },
    onUpdate: ({ editor: instance }) => {
      const markdown = (instance.storage as any).markdown?.getMarkdown?.() ?? "";
      if (normalise(markdown) === normalise(initialValueRef.current)) {
        // No semantic change from the initial value (e.g. a mount-time
        // whitespace normalisation). Don't propagate — would otherwise
        // create a no-op PATCH + audit entry on every modal open.
        return;
      }
      onChange(markdown);
    },
  });

  // Mirror external value changes (e.g. switching projects) without echoing
  // the editor's own keystrokes back through `setContent` — that would move
  // the cursor and feel broken.
  const lastEmittedRef = useRef<string>(value || "");
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming === lastEmittedRef.current) return;
    const current = (editor.storage as any).markdown?.getMarkdown?.() ?? "";
    if (normalise(current) === normalise(incoming)) {
      lastEmittedRef.current = incoming;
      initialValueRef.current = incoming;
      return;
    }
    editor.commands.setContent(incoming);
    lastEmittedRef.current = incoming;
    // Reset the initial-value baseline so a follow-up no-op normalisation
    // doesn't fire onChange after we mirror an external switch.
    initialValueRef.current = incoming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Track every emit so the effect above can ignore echoes.
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      const markdown = (editor.storage as any).markdown?.getMarkdown?.() ?? "";
      lastEmittedRef.current = markdown;
    };
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor]);

  // Keep `editable` synced with the disabled prop (role can flip mid-session
  // in theory; cheap to keep correct).
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  const isEmpty = editor.isEmpty;

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        padding: "8px 12px",
        background: disabled ? "#f5f5f5" : "#fff",
      }}
    >
      {isEmpty && placeholder ? (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            color: "#bfbfbf",
            pointerEvents: "none",
            top: 8,
            left: 12,
          }}
        >
          {placeholder}
        </div>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  );
}
